import dayjs from 'dayjs';
import isEmpty from 'lodash/isEmpty';
import pick from 'lodash/pick';
import random from 'lodash/random';
import { action, computed, makeObservable, observable, reaction, toJS } from 'mobx';

import { BINGO_LAST_ACKNOWLEDGED_EVENT_KEY } from '../../config/constants';
import {
  type MetagameTokenPackageData,
  type MetagameTokenPackageResponse,
  type MetagameResultSocketData,
  type ClaimBingoAnimationState,
  type MetagamesUpcomingEvent,
} from '../../dto/metagames.types';
import { MetaGamesEventType } from '../../dto/metagames.types';
import { postRequest } from '../../services/api';
import { ldClient, resolveFlag } from '../../services/launchDarkly';
import { navigationService } from '../../services/navigation';
import { bingoQueryKeys, queryClient } from '../../services/queryClient';
import type { ApiResponse } from '../../services/types';
import { getErrorMessage } from '../../utils/errors';
import { devError } from '../../utils/log';
import type { LaunchGameData } from '../game/types';
import type { RootStore } from '../RootStore';
import { EnumOpenDialog, type IOpenDialog } from '../ui/types';

import { getBingoCoinTypeFromSocketData, getRewardFromBingoSocketData } from './helpers';
import {
  type WinningEventsData,
  type MetagameBingoGame,
  type MetagameBingoEvent,
  type MetagamesCurrentBingoEvent,
  type CurrentBingoEventStatus,
  type HistoricalBingoEvent,
  type BingoCoinType,
  type BingoStoreTabType,
  type MyCardsModalOptions,
} from './types';

export class BingoStore {
  private readonly rootStore: RootStore;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeObservable(this);

    ldClient?.on('ready', () => {
      this.resolveAndUpdateFlags();
    });

    ldClient?.on('change', (changes) => {
      if ('bingo_enabled' in changes) {
        this.resolveAndUpdateFlags();
      }
    });

    reaction(
      () => ({
        isAuthenticationFlowDone: this.rootStore.authStore.isAuthenticationFlowDone,
        authorized: this.rootStore.authStore.authorized,
      }),
      ({ isAuthenticationFlowDone, authorized }) => {
        if (isAuthenticationFlowDone && authorized) {
          this.resolveAndUpdateFlags();
          this.syncAcknowledgedGameIdsFromLocalStorage();
        }
      },
      { fireImmediately: true },
    );

    // Auto-fetch metagames events when nextUpcomingBingoEvent reaches its start date
    reaction(
      () => ({
        eventId: this.nextUpcomingBingoEvent?.id,
        isFetching: this.isFetchingBingoEvents,
        hasCurrentEvent: !!this.currentBingoEvent,
      }),
      ({ eventId, isFetching, hasCurrentEvent }) => {
        this.clearEventStartTimeout();

        if (isFetching || !eventId || hasCurrentEvent || !this.nextUpcomingBingoEvent) {
          return;
        }

        const msToStart = dayjs(this.nextUpcomingBingoEvent.startDate).diff(dayjs(), 'millisecond');

        const invalidateEvents = () => {
          void queryClient.invalidateQueries({ queryKey: bingoQueryKeys.currentEvent() });
          void queryClient.invalidateQueries({ queryKey: bingoQueryKeys.upcomingEvents() });
        };

        if (msToStart <= 0) {
          // Random delay between 1-5 seconds to avoid race conditions
          const delayMS = random(1000, 5000);

          setTimeout(() => {
            invalidateEvents();
          }, delayMS);

          return;
        }

        const timeoutId = setTimeout(() => {
          invalidateEvents();
          this.clearEventStartTimeout();
        }, msToStart);

        this.setEventStartTimeoutId(timeoutId);
      },
    );

    reaction(
      () => ({
        hasCurrentEvent: !!this.currentBingoEvent,
        eventId: this.currentBingoEvent?.id,
        eventStatus: this.currentBingoEvent ? this.getEventStatus(this.currentBingoEvent) : null,
      }),
      ({ hasCurrentEvent, eventId, eventStatus }) => {
        this.clearPollingTimeout();

        if ((this.previousEventId && !hasCurrentEvent) || (hasCurrentEvent && eventStatus === 'finalized')) {
          void queryClient.invalidateQueries({ queryKey: bingoQueryKeys.historicalEvents() });
          this.setPreviousEventId(undefined);
          return;
        }

        this.setPreviousEventId(eventId);

        const shouldPoll = hasCurrentEvent && eventStatus && ['closed', 'aboutToStart'].includes(eventStatus);

        if (!shouldPoll) {
          return;
        }

        const delayMS = random(5000, 10000);

        const timeoutId = setTimeout(() => {
          void queryClient.invalidateQueries({ queryKey: bingoQueryKeys.currentEvent() });
        }, delayMS);

        this.setPollingTimeoutId(timeoutId);
      },
    );
  }

  @observable
  public claimBingoAnimationState: ClaimBingoAnimationState = {
    bingoAmount: 0,
    enable: false,
  };

  @action
  public setClaimBingoAnimationState(state: Partial<ClaimBingoAnimationState>) {
    this.claimBingoAnimationState = { ...this.claimBingoAnimationState, ...state };
  }

  private DEFAULT_BINGO_STORE_TAB: BingoStoreTabType = 'one-time-cards';

  @observable
  public bingoEnabled = false;

  @observable
  public bingoStoreActiveTab: BingoStoreTabType = this.DEFAULT_BINGO_STORE_TAB;

  @action
  public setBingoStoreActiveTab(tab: BingoStoreTabType) {
    this.bingoStoreActiveTab = tab;
  }

  @action
  public resetBingoStoreActiveTab() {
    this.bingoStoreActiveTab = this.DEFAULT_BINGO_STORE_TAB;
  }

  @observable
  public fetchingHistoricalBingoEvents = false;

  @action
  public setFetchingHistoricalBingoEvents(value: boolean) {
    this.fetchingHistoricalBingoEvents = value;
  }

  @action
  public setBingoEnabled(flag: boolean) {
    this.bingoEnabled = flag;
  }

  @observable
  public bingoStoreItems: MetagameTokenPackageData[] = [];

  @action
  public setBingoStoreItems(items: MetagameTokenPackageData[]) {
    this.bingoStoreItems = items;
  }

  @observable
  public fetchingBingoStorePackages = false;

  @action
  public setFetchingBingoStorePackages(value: boolean) {
    this.fetchingBingoStorePackages = value;
  }

  @observable
  public isBingoPageOpen = false;

  @action
  public setIsBingoPageOpen(value: boolean) {
    this.isBingoPageOpen = value;
  }

  @observable
  public upcomingEvents: MetagameBingoEvent[] = [];

  @action
  public setUpcomingEvents(events: MetagameBingoEvent[]) {
    this.upcomingEvents = events;
  }

  @observable
  public fetchingBingoUpcomingEvents = false;

  @action
  public setFetchingBingoUpcomingEvents(value: boolean) {
    this.fetchingBingoUpcomingEvents = value;
  }

  @observable
  public fetchingCurrentBingoEvent = false;

  @action
  public setFetchingCurrentBingoEvent(value: boolean) {
    this.fetchingCurrentBingoEvent = value;
  }

  @observable
  private showLoadingUpcomingEvents = false;

  @action
  public setShowLoadingUpcomingEvents(value: boolean) {
    this.showLoadingUpcomingEvents = value;
  }

  @observable
  private showLoadingCurrentEvent = false;

  @action
  public setShowLoadingCurrentEvent(value: boolean) {
    this.showLoadingCurrentEvent = value;
  }

  @computed
  public get isFetchingBingoEvents(): boolean {
    return this.showLoadingCurrentEvent || this.showLoadingUpcomingEvents;
  }

  @observable
  public historicalBingoEvents: HistoricalBingoEvent[] = [];

  @action
  public setHistoricalBingoEvents(events: HistoricalBingoEvent[]) {
    this.historicalBingoEvents = events;
  }

  @observable
  public winningEventsData: WinningEventsData | null = null;

  @action
  public setWinningEventsData(eventsData: WinningEventsData | null) {
    this.winningEventsData = eventsData;
  }

  @observable
  public socketBingoResult: MetagameResultSocketData | null = null;

  @action
  public setSocketBingoResult(result: MetagameResultSocketData | null) {
    this.socketBingoResult = result;
  }

  @observable
  private acknowledgedGameIds: Set<string> = new Set();

  @action
  private setAcknowledgedGameId(gameId: string) {
    this.acknowledgedGameIds.add(gameId);
  }

  private getSocketResultToShow(): {
    totalUserReward: number;
    gameId: string;
    coinType: BingoCoinType;
  } | null {
    if (!this.socketBingoResult || this.isLiveBingo) {
      return null;
    }

    const isAcknowledged = this.isEventAcknowledged(this.socketBingoResult.gameId);
    if (isAcknowledged) {
      return null;
    }

    const totalUserReward = getRewardFromBingoSocketData(this.socketBingoResult);
    if (!totalUserReward) {
      return null;
    }

    const coinType = getBingoCoinTypeFromSocketData(this.socketBingoResult);
    return {
      totalUserReward,
      gameId: this.socketBingoResult.gameId,
      coinType,
    };
  }

  private getHistoricalResultToShow(): {
    totalUserReward: number;
    gameId: string;
    coinType: BingoCoinType;
  } | null {
    if (isEmpty(this.historicalBingoEvents)) {
      return null;
    }

    const sortedResults = this.historicalBingoEvents
      .filter((result) => result.endDate)
      .sort((a, b) => dayjs(b.endDate).diff(dayjs(a.endDate)));

    const mostRecentEvent = sortedResults[0];
    if (!mostRecentEvent) {
      return null;
    }

    const isAcknowledged = this.isEventAcknowledged(mostRecentEvent.game.gameId);
    if (isAcknowledged) {
      return null;
    }

    return {
      totalUserReward: mostRecentEvent.game.totalUserReward,
      gameId: mostRecentEvent.game.gameId,
      coinType: mostRecentEvent.coinType,
    };
  }

  @computed
  public get bingoResultToShow(): {
    totalUserReward: number;
    gameId: string;
    coinType: BingoCoinType;
  } | null {
    if (this.isLiveBingo) {
      return null;
    }

    const socketResult = this.getSocketResultToShow();
    if (socketResult) {
      return socketResult;
    }

    return this.getHistoricalResultToShow();
  }

  @computed
  public get hasBingoResultToShow(): boolean {
    return this.bingoResultToShow !== null;
  }

  private getBingoAcknowledgmentKey(): string {
    const userId = this.rootStore.userStore.profile?.userId;
    return `${BINGO_LAST_ACKNOWLEDGED_EVENT_KEY}_${userId}`;
  }

  private isEventAcknowledged(gameId: string): boolean {
    if (this.acknowledgedGameIds.has(gameId)) {
      return true;
    }

    try {
      const bingoAcknowledgmentKey = this.getBingoAcknowledgmentKey();
      const lastAcknowledgedEventId = localStorage.getItem(bingoAcknowledgmentKey);
      return lastAcknowledgedEventId === gameId;
    } catch (error) {
      devError('BingoStore: Error checking event acknowledgment', error);
      return false;
    }
  }

  @action
  private syncAcknowledgedGameIdsFromLocalStorage(): void {
    try {
      this.acknowledgedGameIds.clear();
      const bingoAcknowledgmentKey = this.getBingoAcknowledgmentKey();
      const lastAcknowledgedEventId = localStorage.getItem(bingoAcknowledgmentKey);
      if (lastAcknowledgedEventId) {
        this.acknowledgedGameIds.add(lastAcknowledgedEventId);
      }
    } catch (error) {
      devError('BingoStore: Error syncing acknowledged game IDs from localStorage', error);
    }
  }

  @action
  public acknowledgeBingoEvent(gameId: string): void {
    try {
      const bingoAcknowledgmentKey = this.getBingoAcknowledgmentKey();
      localStorage.setItem(bingoAcknowledgmentKey, gameId);
      this.setAcknowledgedGameId(gameId);
      this.setSocketBingoResult(null);
    } catch (error) {
      devError('BingoStore: Error acknowledging event', error);
    }
  }

  @observable
  public currentMetagameBingoEvent: MetagamesCurrentBingoEvent | undefined = undefined;

  @action
  public setCurrentMetagameBingoEvent(event?: MetagamesCurrentBingoEvent | undefined) {
    this.currentMetagameBingoEvent = event;
  }

  @observable
  public currentOpenBingoEvent: MetagameBingoEvent | undefined = undefined;

  @action
  public setCurrentOpenBingoEvent(event?: MetagameBingoEvent | undefined) {
    this.currentOpenBingoEvent = event;
  }

  @observable
  public selectedPayTableEvent: MetagameBingoEvent | null = null;

  @action
  public setSelectedPayTableEvent(event: MetagameBingoEvent | null) {
    this.selectedPayTableEvent = event;
  }

  @observable
  public selectedResultsEvent: HistoricalBingoEvent | null = null;

  @action
  public setSelectedResultsEvent(event: HistoricalBingoEvent | null) {
    this.selectedResultsEvent = event;
  }

  @observable
  public currentBingoEvent: MetagameBingoEvent | undefined = undefined;

  @observable
  private previousEventId: string | undefined = undefined;

  @action
  private setPreviousEventId(eventId: string | undefined) {
    this.previousEventId = eventId;
  }

  @action
  public setCurrentBingoEvent(event?: MetagameBingoEvent | undefined) {
    if (!event) {
      this.currentBingoEvent = undefined;
      return;
    }

    const msToEndOfEvent = dayjs(event.endDate).diff(dayjs(), 'millisecond');

    if (msToEndOfEvent <= 0) {
      devError('BingoStore: setCurrentBingoEvent called with an event that has already ended');
      this.currentBingoEvent = undefined;
      return;
    }

    this.currentBingoEvent = event;

    this.rootStore.gameStore.sendSlotsGameUiMessage({
      type: 'newMetagamesEvent',
      payload: {
        metagamesEvent: toJS(event),
      },
    });

    setTimeout(() => {
      void this.setCurrentBingoEvent(undefined);
    }, msToEndOfEvent);
  }

  @action
  private updateCurrentBingoEventIsOptedIn() {
    if (this.currentBingoEvent) {
      this.currentBingoEvent = {
        ...this.currentBingoEvent,
        isOptedIn: true,
      };
    }
  }

  @computed
  public get isInsideBingoArea(): boolean {
    return this.isInBingoDialog || this.isBingoPageOpen;
  }

  @computed
  public get isInBingoDialog(): boolean {
    return this.rootStore.uiStore.openFullPageDialog === 'bingo';
  }

  @observable
  public nextCasualBingoEvent: MetagameBingoEvent | null = null;

  @action
  public setNextCasualBingoEvent(event: MetagameBingoEvent | null) {
    this.nextCasualBingoEvent = event;
  }

  @observable
  public nextSweepBingoEvent: MetagameBingoEvent | null = null;

  @action
  public setNextSweepBingoEvent(event: MetagameBingoEvent | null) {
    this.nextSweepBingoEvent = event;
  }

  @observable
  private eventStartTimeoutId: NodeJS.Timeout | null = null;

  @action
  private setEventStartTimeoutId(timeoutId: NodeJS.Timeout | null) {
    this.eventStartTimeoutId = timeoutId;
  }

  @action
  private clearEventStartTimeout() {
    if (this.eventStartTimeoutId) {
      clearTimeout(this.eventStartTimeoutId);
      this.setEventStartTimeoutId(null);
    }
  }

  @observable
  private pollingTimeoutId: NodeJS.Timeout | null = null;

  @action
  private setPollingTimeoutId(timeoutId: NodeJS.Timeout | null) {
    this.pollingTimeoutId = timeoutId;
  }

  @action
  private clearPollingTimeout() {
    if (this.pollingTimeoutId) {
      clearTimeout(this.pollingTimeoutId);
      this.setPollingTimeoutId(null);
    }
  }

  @computed
  public get closestBingoEvent(): MetagameBingoEvent | null {
    if (this.currentBingoEvent) {
      // return current event if it's not finalized and there is a bingo result to show
      if (this.hasBingoResultToShow || !this.isEventFinalized(this.currentBingoEvent)) {
        return this.currentBingoEvent;
      }
    }

    const upcomingEvents = this.upcomingEventsByDate;
    return upcomingEvents.length > 0 ? upcomingEvents[0] : null;
  }

  public openBingoSection(openInStore = false, storeTab?: BingoStoreTabType): void {
    if (openInStore || !this.rootStore.subscriptionStore.finishedCrownPrimeWelcomeTutorial) {
      this.rootStore.wizardStore.initialStep = 0;
      this.rootStore.wizardStore.forceStep(0);
      if (openInStore && storeTab) {
        this.setBingoStoreActiveTab(storeTab);
      }
    } else {
      this.rootStore.wizardStore.initialStep = 1;
      this.rootStore.wizardStore.forceStep(1);
    }

    const isMobile = window.innerWidth <= 768;
    if (isMobile) {
      this.rootStore.uiStore.setOpenFullPageDialog('bingo');
    } else {
      navigationService.go('/bingo');
    }
  }

  public openBingoFullPageDialog(openInStore = false): void {
    this.rootStore.uiStore.setOpenFullPageDialog('bingo');
    if (openInStore) {
      this.rootStore.wizardStore.forceStep(0);
    } else {
      this.rootStore.wizardStore.forceStep(1);
    }
  }

  public openMyCardsModal(options: MyCardsModalOptions): void {
    this.rootStore.uiStore.openDialog = {
      dialog: EnumOpenDialog.isOpenMyCardsDialog,
      options: options as IOpenDialog['options'],
    };
  }

  public closeManageCardsModal() {
    this.rootStore.uiStore.openDialog = {
      dialog: null,
    };
  }

  @computed
  public get upcomingEventsByDate(): MetagameBingoEvent[] {
    return [this.nextCasualBingoEvent, this.nextSweepBingoEvent]
      .filter((event): event is MetagameBingoEvent => event !== null)
      .sort((a, b) => (dayjs(a.startDate).isBefore(dayjs(b.startDate)) ? -1 : 1));
  }

  @computed
  public get nextUpcomingBingoEvent(): MetagameBingoEvent | null {
    if (!this.nextCasualBingoEvent && !this.nextSweepBingoEvent) {
      return null;
    }

    const nextEventByDate = [this.nextCasualBingoEvent, this.nextSweepBingoEvent]
      .filter((event): event is MetagameBingoEvent => event !== null)
      .sort((a, b) => new Date(a.startDate).getTime() - new Date(b.startDate).getTime())[0];

    return nextEventByDate;
  }

  @computed
  public get nextUpcomingEventCoinType(): BingoCoinType | null {
    const nextEvent = this.nextUpcomingBingoEvent;
    return nextEvent ? nextEvent.coinType : null;
  }

  @computed
  public get bingoEventOneTimeCardsBalance(): number {
    const { subscriptionStore } = this.rootStore;
    const { currentPlan, currentSubscription } = subscriptionStore;

    if (!this.nextUpcomingBingoEvent) {
      return 0;
    }

    const { currentSubscriptionCardsCount } = subscriptionStore;

    if (
      !currentPlan ||
      currentPlan.subscriptionStatus !== 'active' ||
      !currentSubscription ||
      // Subscription ends before next event starts
      dayjs(currentSubscription.endDate).isBefore(dayjs(this.nextUpcomingBingoEvent.startDate)) ||
      // Guard against having more cards in the subscription than the event total balance
      this.nextUpcomingBingoEvent.totalBalance < currentSubscriptionCardsCount
    ) {
      return this.nextUpcomingBingoEvent.totalBalance;
    }

    return Math.max(this.nextUpcomingBingoEvent.totalBalance - currentSubscriptionCardsCount, 0);
  }

  @computed
  public get nextCasualBingoEventTotalBalance(): number {
    return this.nextCasualBingoEvent?.totalBalance || 0;
  }

  @computed
  public get nextSweepBingoEventTotalBalance(): number {
    return this.nextSweepBingoEvent?.totalBalance || 0;
  }

  private calculateOneTimeCardsBalance(event: MetagameBingoEvent | null): number {
    if (!event) {
      return 0;
    }

    const { subscriptionStore } = this.rootStore;
    const { currentPlan, currentSubscription, currentSubscriptionCardsCount } = subscriptionStore;

    if (
      !currentPlan ||
      currentPlan.subscriptionStatus !== 'active' ||
      !currentSubscription ||
      dayjs(currentSubscription.endDate).isBefore(dayjs(event.startDate)) ||
      event.totalBalance < currentSubscriptionCardsCount
    ) {
      return event.totalBalance;
    }

    return Math.max(event.totalBalance - currentSubscriptionCardsCount, 0);
  }

  @computed
  public get nextCasualBingoEventOneTimeCardsBalance(): number {
    return this.calculateOneTimeCardsBalance(this.nextCasualBingoEvent);
  }

  @computed
  public get nextSweepBingoEventOneTimeCardsBalance(): number {
    return this.calculateOneTimeCardsBalance(this.nextSweepBingoEvent);
  }

  @computed
  get isLiveBingo() {
    const event = this.currentBingoEvent;
    if (!event) return false;

    return (
      dayjs().isAfter(dayjs(event.startDate)) &&
      dayjs().isBefore(dayjs(event.endDate)) &&
      event.game.openedAt &&
      !event.game.closedAt
    );
  }

  public getEventStatus(event: MetagameBingoEvent | undefined): CurrentBingoEventStatus | null {
    if (!event) return null;

    if (this.isEventLive(event)) return 'live';
    if (this.isEventAboutToStart(event)) return 'aboutToStart';
    if (this.isEventClosed(event)) return 'closed';
    if (this.isEventFinalized(event)) return 'finalized';
    return null;
  }

  @computed
  get currentEventStatus(): CurrentBingoEventStatus | null {
    return this.getEventStatus(this.currentBingoEvent);
  }

  public isEventLive(event: MetagameBingoEvent): boolean {
    return (
      dayjs().isAfter(dayjs(event.startDate)) &&
      dayjs().isBefore(dayjs(event.endDate)) &&
      !!event.game.openedAt &&
      !event.game.closedAt
    );
  }

  public isEventUpcoming(event: MetagameBingoEvent): boolean {
    return dayjs(event.startDate).isAfter(dayjs());
  }

  public isEventAboutToStart(event: MetagameBingoEvent): boolean {
    return dayjs().isAfter(dayjs(event.startDate)) && dayjs().isBefore(dayjs(event.endDate)) && !event.game.openedAt;
  }

  public isEventClosed(event: MetagameBingoEvent): boolean {
    return !!event.game.closedAt && !event.game.finalizedAt;
  }

  public isEventFinalized(event: MetagameBingoEvent): boolean {
    return !!event.game.finalizedAt;
  }

  @computed
  get hasEverParticipatedInBingo(): boolean {
    const historicalBingoEventsWithBalance = this.historicalBingoEvents.find((event) => !!event.totalBalance);
    const hasBalanceForCurrentEvent = !!this.currentBingoEvent?.totalBalance;

    const hasParticipatedInBingo =
      !!historicalBingoEventsWithBalance || hasBalanceForCurrentEvent || this.isParticipatingInClosestBingoEvent;

    return hasParticipatedInBingo;
  }

  @computed
  get isParticipatingInClosestBingoEvent() {
    const { subscriptionStore } = this.rootStore;
    const { hasActiveSubscription } = subscriptionStore;

    return hasActiveSubscription || !!this.currentBingoEvent?.totalBalance || this.bingoEventOneTimeCardsBalance > 0;
  }

  @computed
  get isParticipatingInNextBingoEvent() {
    const { subscriptionStore } = this.rootStore;
    const { subscriptionStatus } = subscriptionStore;

    return subscriptionStatus === 'active' || this.bingoEventOneTimeCardsBalance > 0;
  }

  private resolveAndUpdateFlags() {
    if (!this.rootStore.authStore.authorized || !this.rootStore.userStore.profile) {
      this.setBingoEnabled(false);
      return;
    }

    const bingoFlag = resolveFlag('bingo_enabled', true);

    this.setBingoEnabled(bingoFlag);
  }

  public openBingoStore(storeTab?: BingoStoreTabType): void {
    if (this.isInsideBingoArea) {
      this.rootStore.wizardStore.forceStep(0);
      this.rootStore.wizardStore.initialStep = 0;
      this.setBingoStoreActiveTab(storeTab ?? this.DEFAULT_BINGO_STORE_TAB);
      return;
    }

    this.openBingoSection(true, storeTab);
  }

  public convertMetagameEventToBingoEvent(event?: MetagamesCurrentBingoEvent): MetagameBingoEvent | undefined {
    if (!event) {
      return undefined;
    }

    const currency = event.cash ? 'cash' : 'gc';
    const coinType = currency === 'cash' ? 'CASH' : 'GC';

    const eventByCurrency = event[currency];

    if (!eventByCurrency?.game) {
      return undefined;
    }

    return {
      ...pick(event, ['id', 'startDate', 'endDate']),
      coinType,
      gameId: eventByCurrency.gameId,
      isOptedIn: !!eventByCurrency.isOptedIn,
      currencySymbol: eventByCurrency.currencySymbol,
      totalBalance: eventByCurrency.totalBalance,
      game: eventByCurrency.game,
    };
  }

  @action
  public updateBingoStorePackagesFromQuery(packages: MetagameTokenPackageData[]) {
    this.setBingoStoreItems(packages);
  }

  /**
   * Gets the price for purchasing bingo tokens
   * @param quantity - The number of bingo tokens to purchase
   */
  public async getBingoTokenPrice(quantity: number): Promise<MetagameTokenPackageResponse> {
    return this.rootStore.metagamesStore.getTokenPackagePrice(MetaGamesEventType.BINGO, quantity);
  }

  @action
  public updateHistoricalEventsFromQuery(historicalEvents: HistoricalBingoEvent[]) {
    this.setHistoricalBingoEvents(historicalEvents);
    this.updateWinningEventsData(historicalEvents);
  }

  @action
  private updateWinningEventsData(historicalEvents: HistoricalBingoEvent[]): void {
    const winningEventsData = this.processWinningEventsData(historicalEvents);
    this.setWinningEventsData(winningEventsData);
  }

  private processWinningEventsData(historicalEvents: HistoricalBingoEvent[]): WinningEventsData {
    const events: Array<{ event: HistoricalBingoEvent; coinType: BingoCoinType }> = [];
    let totalGcWins = 0;
    let totalCashWins = 0;

    historicalEvents
      .filter((event) => event.game.totalUserReward > 0)
      .forEach((event) => {
        events.push({ event, coinType: event.coinType });
        if (event.coinType === 'GC') {
          totalGcWins += event.game.totalUserReward;
        } else {
          totalCashWins += event.game.totalUserReward;
        }
      });

    const sortedEvents = events.sort(
      (a, b) => new Date(b.event.startDate).getTime() - new Date(a.event.startDate).getTime(),
    );

    return {
      totalGcWins,
      totalCashWins,
      events: sortedEvents,
    };
  }

  @action
  public updateUpcomingEventsFromQuery(upcomingEvents: MetagamesUpcomingEvent<MetagameBingoGame>[]) {
    const upcomingBingoEventData = upcomingEvents.map<MetagameBingoEvent>((event) => ({
      id: event.id,
      isOptedIn: event.isOptedIn,
      gameId: event.gameId,
      startDate: event.startDate,
      endDate: event.endDate,
      coinType: event.coinType,
      currencySymbol: event.currencySymbol,
      totalBalance: event.totalBalance,
      game: event.game,
    }));

    const sortedByDateEvents = upcomingBingoEventData.sort((a, b) =>
      dayjs(a.startDate).isBefore(dayjs(b.startDate)) ? -1 : 1,
    );

    this.setUpcomingEvents(sortedByDateEvents);

    const casualEvent = sortedByDateEvents.find((event) => event.game.eventType === 'CASUAL') || null;
    const sweepEvent = sortedByDateEvents.find((event) => event.game.eventType === 'SWEEP') || null;

    this.setNextCasualBingoEvent(casualEvent);
    this.setNextSweepBingoEvent(sweepEvent);
  }

  public closeGame() {
    this.setCurrentOpenBingoEvent();
    this.rootStore.metagamesStore.onMetagameDialogClose?.();
    this.rootStore.uiStore.setCloseDialog();
    this.rootStore.metagamesStore.clearIframeRef();
    this.rootStore.metagamesStore.setCurrentOpenMetagameEvent(undefined);
  }

  public onGameEnded() {
    this.markCurrentBingoEventAsClosed();
  }

  public async enterLiveGame() {
    if (!this.currentBingoEvent) {
      devError('BingoStore: Error starting bingo game: currentBingoEvent not found');
      return;
    }

    const { coinType, id: eventId } = this.currentBingoEvent;

    if (!this.currentBingoEvent.isOptedIn) {
      this.updateCurrentBingoEventIsOptedIn();

      void this.rootStore.metagamesStore.optInToEvent(eventId, coinType);
    }

    await this.startBingoGame();
  }

  @action
  private markCurrentBingoEventAsClosed() {
    if (!this.currentBingoEvent || !this.currentBingoEvent.game || this.currentBingoEvent.game.closedAt) {
      return;
    }

    this.setCurrentBingoEvent({
      ...this.currentBingoEvent,
      game: {
        ...this.currentBingoEvent.game,
        closedAt: dayjs().toISOString(),
      },
    });
  }

  public async startBingoGame() {
    try {
      if (!this.currentBingoEvent) {
        devError('BingoStore: Error starting bingo game: currentBingoEvent not found');
        return;
      }

      const { coinType, gameId } = this.currentBingoEvent;

      const launchGameResponse = await postRequest<ApiResponse<LaunchGameData>>('user/launch-game', {
        coin: coinType,
        gameId,
      });

      if (!launchGameResponse.data?.data) {
        devError('BingoStore: Error starting bingo game: launchGameResponse not found');
        return;
      }

      const { metagames } = launchGameResponse.data.data;

      if (!metagames) {
        devError('BingoStore: Error starting bingo game: metagames not found');
        return;
      }

      const { token, path } = metagames;
      const { gameUiFlag } = this.rootStore.gameStore;

      const metagamesUrl = this.rootStore.gameUiStore.getGameUiURL(path, {
        metagamesToken: gameUiFlag ? token : undefined,
        monetizationEventsToken: gameUiFlag ? token : undefined,
        token: gameUiFlag ? undefined : token,
        coinType,
      });

      const metagamesEventToSet = {
        ...this.currentBingoEvent,
        token,
        path,
        url: metagamesUrl,
      };

      this.setCurrentOpenBingoEvent(metagamesEventToSet);
      this.rootStore.metagamesStore.setCurrentOpenMetagameEvent(this.currentMetagameBingoEvent);

      this.rootStore.uiStore.setOpenDialog({
        dialog: EnumOpenDialog.isOpenMetagamesDialog,
        options: { type: MetaGamesEventType.BINGO },
      });
    } catch (error) {
      const errorMessage = getErrorMessage(error);
      devError(`BingoStore: Error starting bingo game: ${errorMessage}`);
    }
  }

  @action
  public startClaimBingoAnimation({
    bingoAmount,
    onAnimationEnd,
    coinType,
  }: {
    bingoAmount: number;
    onAnimationEnd?: () => Promise<void> | void;
    coinType?: BingoCoinType;
  }) {
    if (this.claimBingoAnimationState.enable) {
      return;
    }

    let hasEnded = false;

    this.setClaimBingoAnimationState({
      bingoAmount,
      enable: true,
      coinType,
      onAnimationEnd: () => {
        if (hasEnded) {
          return;
        }
        hasEnded = true;
        this.setClaimBingoAnimationState({ enable: false });
        void onAnimationEnd?.();
      },
    });
  }

  @action
  public resetAll() {
    this.setBingoEnabled(false);
    this.setIsBingoPageOpen(false);
    this.setUpcomingEvents([]);
    this.setFetchingBingoUpcomingEvents(false);
    this.setFetchingCurrentBingoEvent(false);
    this.setShowLoadingUpcomingEvents(false);
    this.setShowLoadingCurrentEvent(false);
    this.setHistoricalBingoEvents([]);
    this.setWinningEventsData(null);
    this.setSocketBingoResult(null);
    this.acknowledgedGameIds.clear();
    this.setPreviousEventId(undefined);
    this.setBingoStoreActiveTab(this.DEFAULT_BINGO_STORE_TAB);
  }
}
